import { useEffect, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { exchangeCodeForToken, login } from '../services/auth';
import { clearStoredToken, loadStoredToken, storeToken } from '../utils/tokenStore';
import AuthStatusShell from '../components/AuthStatusShell';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const inFlight = useRef(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const provider = searchParams.get('provider') || undefined;
    if (!code) {
      setError('Sign-in did not return an authorization code.');
      return;
    }

    // React StrictMode (dev) mounts effects twice and the OAuth `code`
    // is single-use, so start the exchange exactly once per code. A
    // different code (fresh login) starts a new exchange.
    if (!inFlight.current || inFlight.current.code !== code) {
      inFlight.current = { code, promise: exchangeCodeForToken(code, provider) };
    }
    const controller = new AbortController();

    inFlight.current.promise
      .then((profile) => {
        storeToken(profile);
        return refresh();
      })
      .then(() => navigate('/', { replace: true }))
      .catch((err) => {
        if (controller.signal.aborted) return;
        // Only clear the token if none is present, so a successful
        // exchange from a concurrent run is not wiped out (which
        // would strip the Bearer token from every API call).
        if (!loadStoredToken()) clearStoredToken();
        setError(err?.message || 'Sign-in failed.');
      });

    return () => controller.abort();
  }, [navigate, refresh, searchParams]);

  if (error) {
    return (
      <AuthStatusShell
        title="Admin sign-in was denied"
        description={error}
        status="No admin session was created"
        actionLabel="Try again"
        onAction={login}
        footnote="Your account must have the admin role to enter this workspace."
      />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      {error ? <Alert type="error" message={error} /> : <Spin size="large" tip="Completing sign-in..." />}
    </div>
  );
}